// Lightweight IndexedDB cache with stale-while-revalidate semantics
// Store: DB 'r360Cache_<version>', object store 'kv' with keyPath 'key'

import CLIENT_VERSION from "../versionState";

const CACHE_SCHEMA_VERSION = "v3_ingest_completion_refresh_20260523";
const DB_NAME = `r360Cache_${CLIENT_VERSION || "dev"}_${CACHE_SCHEMA_VERSION}`;
const STORE = 'kv';
const VERSION = 1;
const inflightByKey = new Map();
let cacheScope = "anonymous";

export function setCacheScope(scope) {
  const next = String(scope || "anonymous");
  if (next === cacheScope) return;
  cacheScope = next;
  inflightByKey.clear();
}

function scopedKey(key) {
  return `${cacheScope}:${key}`;
}

function abortError() {
  if (typeof DOMException === 'function') {
    return new DOMException('The operation was aborted.', 'AbortError');
  }
  const err = new Error('The operation was aborted.');
  err.name = 'AbortError';
  return err;
}

function throwIfAborted(signal) {
  if (signal?.aborted) throw abortError();
}

function abortableForCaller(promise, signal) {
  if (!signal) return promise;
  if (signal.aborted) return Promise.reject(abortError());

  return new Promise((resolve, reject) => {
    const onAbort = () => {
      signal.removeEventListener('abort', onAbort);
      reject(abortError());
    };

    signal.addEventListener('abort', onAbort, { once: true });
    promise.then(
      (value) => {
        signal.removeEventListener('abort', onAbort);
        if (signal.aborted) reject(abortError());
        else resolve(value);
      },
      (err) => {
        signal.removeEventListener('abort', onAbort);
        reject(err);
      }
    );
  });
}

function openDb() {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE)) {
        db.createObjectStore(STORE, { keyPath: 'key' });
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

async function withStore(mode, fn) {
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE, mode);
    const st = tx.objectStore(STORE);
    const res = fn(st);
    tx.oncomplete = () => resolve(res);
    tx.onerror = () => reject(tx.error);
  });
}

export async function idbGet(key) {
  return withStore('readonly', (st) => new Promise((resolve, reject) => {
    const req = st.get(key);
    req.onsuccess = () => resolve(req.result || null);
    req.onerror = () => reject(req.error);
  }));
}

export async function idbSet(key, value, ttlMs) {
  const now = Date.now();
  const rec = { key, value, ts: now, exp: ttlMs ? (now + ttlMs) : null };
  return withStore('readwrite', (st) => st.put(rec));
}

export async function clearDashboardCache() {
  inflightByKey.clear();
  try {
    await withStore('readwrite', (st) => st.clear());
  } catch {}
}

function deleteDatabase(name) {
  return new Promise((resolve) => {
    try {
      if (!name || typeof indexedDB?.deleteDatabase !== 'function') {
        resolve(false);
        return;
      }
      const req = indexedDB.deleteDatabase(name);
      req.onsuccess = () => resolve(true);
      req.onerror = () => resolve(false);
      req.onblocked = () => resolve(false);
    } catch {
      resolve(false);
    }
  });
}

export async function clearAllDashboardCaches() {
  inflightByKey.clear();
  await clearDashboardCache();

  try {
    if (typeof indexedDB?.databases !== 'function') return;
    const dbs = await indexedDB.databases();
    const names = (dbs || [])
      .map((db) => db?.name)
      .filter((name) => name && name !== DB_NAME && String(name).startsWith('r360Cache_'));
    await Promise.all(names.map((name) => deleteDatabase(name)));
  } catch {
    // Current-version cache was already cleared above; old cache DB cleanup is best effort.
  }
}

export async function staleWhileRevalidate({ key, fetcher, ttlMs = 30 * 60 * 1000, onUpdate, signal }) {
  const cacheKey = scopedKey(key);
  const rec = await idbGet(cacheKey);
  throwIfAborted(signal);

  const now = Date.now();
  const hasAny = !!rec;
  const isFresh = hasAny && (!rec.exp || rec.exp > now);

  if (isFresh) return rec.value;

  let refresh = inflightByKey.get(cacheKey);
  if (!refresh) {
    refresh = Promise.resolve()
      .then(() => fetcher())
      .then(async (fresh) => {
        try { await idbSet(cacheKey, fresh, ttlMs); } catch {}
        return fresh;
      })
      .finally(() => {
        if (inflightByKey.get(cacheKey) === refresh) inflightByKey.delete(cacheKey);
      });
    inflightByKey.set(cacheKey, refresh);
  }

  // Return immediately if we have any cached value while the shared refresh runs.
  refresh.then((fresh) => {
    if (typeof onUpdate === 'function' && !(signal && signal.aborted)) onUpdate(fresh);
  }).catch(() => {});
  if (hasAny) return rec.value;

  return abortableForCaller(refresh, signal);
}
